import { useState } from "react";
import { Send, CheckCircle, AlertCircle, Play, Phone, Mail, MapPin } from "lucide-react";

export default function VirtualTourContact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", projectName: "la velora", website_name: "lavelora.in" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) {
      e.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = "Enter a valid email";
    }
    if (!form.phone.trim()) {
      e.phone = "Phone number is required";
    } else if (!/^[6-9]\d{9}$/.test(form.phone.replace(/\s/g, ""))) {
      e.phone = "Enter a valid 10-digit phone number";
    }
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Simulate API call or backend integration
    
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      const response = await fetch(
      "https://ycb1sb6t8e.execute-api.us-west-2.amazonaws.com/default/LeadStorw",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      },
    );
    if (!response.ok) {
      // Handle error
      console.error("Failed to submit enquiry");
      return;
    } else {
       setSubmitted(true);
      setForm({ name: "", email: "", phone: "", projectName: "la velora", website_name: "lavelora.in" });
    }
     
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <section
      id="contact"
      data-testid="virtual-tour-contact-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left - Virtual Tour */}
          <div>
            <p className="overline mb-4">Virtual Tour</p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#1A1A1A] mb-6">
              Experience La Valora
              <br />
              <span className="italic text-[#C5A059]">from anywhere</span>.
            </h2>
            <div className="gold-line mb-8" />

            {/* Video embed placeholder */}
            <div
              data-testid="virtual-tour-embed"
              className="relative bg-[#1A1A1A] w-full aspect-video flex items-center justify-center group cursor-pointer overflow-hidden"
            >
              <img
                src="../images/build1.png"
                alt="Virtual Tour Preview"
                className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-40 transition-opacity duration-500"
              />
              <div className="relative z-10 flex flex-col items-center">
                <div className="w-20 h-20 rounded-full bg-[#C5A059] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Play size={32} className="text-white ml-1" fill="white" />
                </div>
                <p className="text-white text-sm uppercase tracking-[0.2em] mt-4">
                  Watch Virtual Tour
                </p>
              </div>
            </div>

            {/* Contact Info */}
            <div className="mt-10 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#C5A059]/10">
                  <Phone size={18} className="text-[#C5A059]" />
                </div>
                <div>
                  <p className="text-xs text-[#5C5C59] uppercase tracking-wider">Call Us</p>
                  <a href="tel:+919030145871" className="text-[#1A1A1A] font-medium hover:text-[#C5A059] transition-colors">
                    +91 9030145871
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div>
            <p className="overline mb-4">Get In Touch</p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#1A1A1A] mb-6">
              Schedule your
              <br />
              <span className="italic text-[#C5A059]">exclusive visit</span>.
            </h2>
            <div className="gold-line mb-10" />

            {submitted ? (
              <div data-testid="form-success" className="bg-[#C5A059]/5 border border-[#C5A059]/20 p-10 text-center">
                <CheckCircle size={48} className="text-[#C5A059] mx-auto mb-4" />
                <h3 className="font-serif text-2xl text-[#1A1A1A] mb-2">Thank You!</h3>
                <p className="text-[#5C5C59]">
                  We&apos;ve received your inquiry. Our team will reach out to you shortly.
                </p>
                <button
                  data-testid="form-reset-btn"
                  onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", projectName: "la velora", website_name: "lavelora.in" }); }}
                  className="mt-6 text-sm text-[#C5A059] uppercase tracking-widest hover:underline"
                >
                  Send Another Inquiry
                </button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-8">
                {/* Name */}
                <div>
                  <input
                    data-testid="form-name"
                    type="text"
                    placeholder="Your Full Name"
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="w-full border-b border-[#E8E4D9] bg-transparent py-3 focus:outline-none focus:border-[#C5A059] transition-colors text-[#1A1A1A] placeholder:text-[#5C5C59]/50"
                  />
                  {errors.name && (
                    <p data-testid="error-name" className="flex items-center gap-1 text-red-500 text-xs mt-2">
                      <AlertCircle size={12} /> {errors.name}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <input
                    data-testid="form-email"
                    type="email"
                    placeholder="Email Address"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="w-full border-b border-[#E8E4D9] bg-transparent py-3 focus:outline-none focus:border-[#C5A059] transition-colors text-[#1A1A1A] placeholder:text-[#5C5C59]/50"
                  />
                  {errors.email && (
                    <p data-testid="error-email" className="flex items-center gap-1 text-red-500 text-xs mt-2">
                      <AlertCircle size={12} /> {errors.email}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <input
                    data-testid="form-phone"
                    type="tel"
                    placeholder="Phone Number"
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    className="w-full border-b border-[#E8E4D9] bg-transparent py-3 focus:outline-none focus:border-[#C5A059] transition-colors text-[#1A1A1A] placeholder:text-[#5C5C59]/50"
                  />
                  {errors.phone && (
                    <p data-testid="error-phone" className="flex items-center gap-1 text-red-500 text-xs mt-2">
                      <AlertCircle size={12} /> {errors.phone}
                    </p>
                  )}
                </div>

                <button
                  data-testid="form-submit-btn"
                  type="submit"
                  className="flex items-center justify-center gap-3 w-full bg-[#C5A059] text-white py-4 uppercase tracking-widest text-sm hover:bg-[#B08D4C] transition-colors duration-300"
                >
                  <Send size={16} />
                  Submit Enquiry
                </button>

                <p className="text-xs text-[#5C5C59]/60 text-center">
                  By submitting, you agree to receive communications from La Valora.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
